import {
  type GetHirerNotificationPreferenceResult,
  useSetDefaultNotificationPreference,
} from '@seek/hirer-notification-preference';
import {
  type GetHirerTeamMemberJobAccessListResult,
  useGetHirerTeamMemberJobAccessAccessList,
} from '@seek/hirer-team-member-job-access';
import { useTranslations } from '@vocab/react';
import {
  Heading,
  List,
  Text,
  Button,
  Stack,
  Actions,
} from 'braid-design-system';
import React, { useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { NOTIFICATION_SET_PREFERENCES_BTN_PRESSED } from 'src/app/shared/dataAnalytics/schema/constants';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import {
  dispatchSetSavedNotificationPreference,
  dispatchSetSavedTeamMemberJobAccessList,
  dispatchStartSettingMode,
} from 'src/app/v1/state/dispatcher/notificationsSetting';
import type { AppState } from 'src/types/appState';

import { CandidateManagementLink } from '../CandidateManageLink';
import { CurrentNotificationPreference } from '../CurrentNotificationPreference';
import SaveMessage from '../SaveMessage';

import translations from './.vocab';

interface Props {
  jobId: string;
}
export const NotificationsSettingTask = ({ jobId }: Props) => {
  const {
    isSettingCompleted,
    seekAdvertiserId,
    hasSavedNotificationPreference,
    hasSavedTeamMemberJobAccessList,
  } = useSelector(
    (state: AppState) => ({
      isSettingCompleted: state.session.notificationsSetting.isSettingCompleted,
      seekAdvertiserId: state.session.seekAdvertiserId,
      hasSavedNotificationPreference: Boolean(
        state.session.notificationsSetting.savedNotificationPreference,
      ),
      hasSavedTeamMemberJobAccessList: Boolean(
        state.session.notificationsSetting.savedTeamMemberJobAccessList,
      ),
    }),
    shallowEqual,
  );
  const { t } = useTranslations(translations);

  const dispatch = useDispatch();
  const onSelectSetNotifications = () => {
    const eventData = {
      eventName: NOTIFICATION_SET_PREFERENCES_BTN_PRESSED,
    };
    getTrackingAdapter.sendLinkEvent(eventData);

    dispatch(dispatchStartSettingMode());
  };
  const setDefaultNotificationPreference =
    useSetDefaultNotificationPreference();
  const getHirerTeamMembers = useGetHirerTeamMemberJobAccessAccessList();

  useEffect(() => {
    if (!hasSavedTeamMemberJobAccessList && seekAdvertiserId) {
      getHirerTeamMembers(
        ({
          error,
          teamMemberJobAccessList,
        }: GetHirerTeamMemberJobAccessListResult) => {
          if (!error) {
            dispatch(
              dispatchSetSavedTeamMemberJobAccessList(teamMemberJobAccessList),
            );
          }
        },
      );
    }
    if (!hasSavedNotificationPreference) {
      setDefaultNotificationPreference(
        ({
          notificationPreference,
          error,
        }: GetHirerNotificationPreferenceResult) => {
          if (!error) {
            dispatch(
              dispatchSetSavedNotificationPreference(notificationPreference),
            );
          }
        },
      );
    }
  }, [
    getHirerTeamMembers,
    setDefaultNotificationPreference,
    dispatch,
    hasSavedNotificationPreference,
    hasSavedTeamMemberJobAccessList,
    seekAdvertiserId,
  ]);

  return (
    <Stack space="large">
      <Heading level="3">
        {isSettingCompleted
          ? t('notifications-setting-task-saved-heading-text')
          : t('notifications-setting-task-set-up-heading-text')}
      </Heading>
      {isSettingCompleted ? (
        <Text tone="secondary">
          <CandidateManagementLink jobId={jobId} />
        </Text>
      ) : (
        <List type="number">
          <Stack space="small">
            <Text weight="strong">
              {t('notifications-setting-task-secondary-text-step-1')}
            </Text>
            <CurrentNotificationPreference />
          </Stack>
          <Stack space="small">
            <Text weight="strong">
              {t('notifications-setting-task-secondary-text-step-2')}
            </Text>
            <Text>
              {t(
                'notifications-setting-task-secondary-text-step-2-description',
              )}
            </Text>
          </Stack>
        </List>
      )}
      {isSettingCompleted ? (
        <SaveMessage text={t('notifications-setting-task-save-message-text')} />
      ) : (
        <Actions>
          <Button
            onClick={onSelectSetNotifications}
            variant="ghost"
            type="button"
            id="set-notification-preferences"
          >
            {t('notifications-setting-task-set-preference-button-text')}
          </Button>
        </Actions>
      )}
    </Stack>
  );
};
